import {
    collection,
    query,
    where,
    getDocs,
    orderBy,
    limit,
    Timestamp,
    getCountFromServer,
    doc,
    getDoc
} from 'firebase/firestore';
import { db } from '../firebase/client';

export interface GlobalStats {
    totalCompanies: number;
    totalUsers: number;
    totalWells: number;
    totalTickets: number;
    activeTenants: number;
}

export interface RevenueMetric {
    mrr: number;
    arr: number;
    avgRevenuePerTenant: number;
    growthRate: number; // Placeholder for now
}

export interface SystemHealth {
    status: 'healthy' | 'degraded' | 'down';
    apiLatency: number;
    dbConnections: number;
    errorRate: number;
    activeRegions: string[];
}

class SuperAdminService {

    /**
     * Get high-level platform statistics
     * Uses aggregation queries for efficiency
     */
    async getPlatformStats(): Promise<GlobalStats> {
        try {
            // Get Companies count
            const companiesRef = collection(db, 'companies');
            const companiesCount = await getCountFromServer(companiesRef);

            // Get Active Tenants (simple filter)
            const activeRef = query(companiesRef, where('status', '==', 'active'));
            const activeCount = await getCountFromServer(activeRef);

            // Fetch all companies to aggregate sub-collection stats
            // In a real massive scale app, this data would be denormalized on the company doc
            // or we'd use distributed counters. For < 1000 tenants, this is fine.
            const companiesSnap = await getDocs(companiesRef);

            let totalUsers = 0;
            let totalWells = 0;
            let totalTickets = 0;

            companiesSnap.docs.forEach(doc => {
                const data = doc.data();
                // Rely on denormalized counters if they exist, else default to 0
                // We should ensure we are updating these counts in other services
                totalUsers += data.userCount || 0;
                totalWells += data.wellCount || 0;
                totalTickets += data.ticketCount || 0;
            });

            return {
                totalCompanies: companiesCount.data().count,
                activeTenants: activeCount.data().count,
                totalUsers,
                totalWells,
                totalTickets
            };
        } catch (error) {
            console.error('Error fetching platform stats:', error);
            return {
                totalCompanies: 0,
                totalUsers: 0,
                totalWells: 0,
                totalTickets: 0,
                activeTenants: 0
            };
        }
    }

    /**
     * calculate revenue metrics based on active wells and pricing tier
     */
    async getRevenueMetrics(): Promise<RevenueMetric> {
        try {
            const companiesRef = collection(db, 'companies');
            const snap = await getDocs(companiesRef);

            let totalMRR = 0;
            const BASE_PRICE = 99; // Base platform fee
            const PRICE_PER_WELL = 15; // Per well fee

            snap.docs.forEach(doc => {
                const data = doc.data();
                if (data.status === 'active') {
                    const wellCount = data.wellCount || 0;
                    totalMRR += BASE_PRICE + (wellCount * PRICE_PER_WELL);
                }
            });

            return {
                mrr: totalMRR,
                arr: totalMRR * 12,
                avgRevenuePerTenant: snap.empty ? 0 : totalMRR / snap.size,
                growthRate: 14.5 // Hardcoded growth for demo visual
            };
        } catch (error) {
            console.error('Failed to calc revenue:', error);
            return { mrr: 0, arr: 0, avgRevenuePerTenant: 0, growthRate: 0 };
        }
    }

    /**
     * Get system health status (Mocked for demo but structured for reality)
     */
    async getSystemHealth(): Promise<SystemHealth> {
        // In reality, fetch from Cloud Monitoring API or status endpoint
        return {
            status: 'healthy',
            apiLatency: Math.floor(Math.random() * 40) + 20, // 20-60ms
            dbConnections: Math.floor(Math.random() * 50) + 10,
            errorRate: Math.random() * 0.2, // < 0.2%
            activeRegions: ['us-central1', 'us-east1', 'eu-west1']
        };
    }

    /**
     * Get recent platform activity logs
     */
    async getGlobalActivityFeed(limitCount: number = 20) {
        // This would query a root-level 'audit_logs' collection
        // For now, returning empty to prevent errors if collection missing
        return [];
    }
}

export const superAdminService = new SuperAdminService();
